// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Trade Utilities
 * 
 * Helper functions for calculating trade prices and profits.
 * Includes stock-based pricing modifiers.
 */

import tradeGoods from '../../config/TradeGoodsConfig';
import * as MarketInventory from './MarketInventoryManager';
import { getFactionPriceMultiplier } from './ReputationManager';

const { getDemandModifier } = MarketInventory;

// Map universe IDs to faction origin strings used in AlienSpeciesConfig
const UNIVERSE_FACTION_MAP = {
  milkyway: 'Original',
  pokemon: 'Pokémon',
  startrek: null,  // Milky Way aliens use 'Star Trek' origin but universe is separate
  starwars: 'Star Wars',
  doctorwho: 'Doctor Who',
  marvel: 'Marvel Comics',
  dc: 'DC Comics',
  halo: 'Halo',
  masseffect: 'Mass Effect',
  warhammer40k: 'Warhammer 40K',
  neonmetropolis: 'Cyberpunk',
  discworld: 'Discworld',
  improbability: 'Douglas Adams',
  marioworld: 'Mario World',
  sonicworld: 'Sonic Universe',
  digiworld: 'Digiworld',
  zelda: 'Zelda Universe',
};

/**
 * Get the faction origin for the current universe.
 * @returns {string|null}
 */
const getCurrentFaction = () => {
  try {
    const state = JSON.parse(localStorage.getItem('universeState') || '{}');
    const universeId = state.currentUniverse;
    return universeId ? (UNIVERSE_FACTION_MAP[universeId] || null) : null;
  } catch { return null; }
};

/**
 * Determine location trade type based on config or name
 */
export const getLocationType = (location) => {
  // First check if location has explicit tradeType property
  if (location.tradeType) {
    return location.tradeType;
  }
  
  // Fallback to name-based detection for backwards compatibility
  const name = location.name.toLowerCase();
  const distance = location.distance || 0;
  
  // Check for station
  if (location.type === 'station' || name.includes('station')) {
    return 'station';
  }
  
  // Check for specific planet types in name
  if (name.includes('mining') || name.includes('asteroid')) return 'mining';
  if (name.includes('industrial') || name.includes('factory')) return 'industrial';
  if (name.includes('agricultural') || name.includes('farm')) return 'agricultural';
  
  // Remote planets (far from Sol)
  if (distance > 300) return 'remote';
  
  return 'default';
};

/**
 * Calculate price for a trade good at a location
 * Includes base price, location modifier, distance modifier, stock level, and random variance
 */
export const calculatePrice = (goodId, location, isBuying = true, locationId = null) => {
  const good = tradeGoods[goodId];
  if (!good) return 0;
  
  const locationType = getLocationType(location);
  const locationMod = good.locationModifiers[locationType] || good.locationModifiers.default;
  
  // Distance modifier: farther from Sol = slightly higher prices
  const distanceMod = 1 + (location.distance / 1000) * 0.1; // +10% per 1000 LY
  
  // Stock-based price modifier (if locationId provided)
  let stockMod = 1.0;
  if (locationId) {
    const stockLevel = MarketInventory.getStockLevel(locationId, goodId, location);
    stockMod = MarketInventory.getStockPriceModifier(stockLevel);
  }
  
  // Random variance for market fluctuations (reduced when stock system active)
  const varianceAmount = locationId ? good.priceVariance * 0.5 : good.priceVariance;
  const variance = 1 + (Math.random() * 2 - 1) * varianceAmount;
  
  // Faction reputation modifier
  const faction = getCurrentFaction();
  const reputationMod = faction ? getFactionPriceMultiplier(faction) : 1.0;
  
  // Global demand modifier (player trading patterns)
  const demandMod = getDemandModifier(goodId);
  
  // Calculate final price
  let price = good.basePrice * locationMod * distanceMod * stockMod * variance * reputationMod * demandMod;
  
  // Buying from station is more expensive, selling is cheaper
  if (isBuying) {
    price *= 1.1; // 10% markup when buying
  } else {
    price *= 0.9; // 10% markdown when selling
  }
  
  return Math.round(price);
};

/**
 * Calculate potential profit for a trade route
 */
export const calculateProfit = (goodId, buyLocation, sellLocation, quantity) => {
  const buyPrice = calculatePrice(goodId, buyLocation, true);
  const sellPrice = calculatePrice(goodId, sellLocation, false);
  
  const profit = (sellPrice - buyPrice) * quantity;
  const profitPercent = ((sellPrice - buyPrice) / buyPrice) * 100;
  
  return {
    buyPrice,
    sellPrice,
    profit,
    profitPercent,
    totalCost: buyPrice * quantity,
    totalRevenue: sellPrice * quantity
  };
};

/**
 * Get best trade goods to buy at current location
 * Returns goods sorted by potential profit
 */
export const getBestBuys = (currentLocation, targetLocation = null) => {
  const goods = Object.values(tradeGoods);
  
  return goods.map(good => {
    const buyPrice = calculatePrice(good.id, currentLocation, true);
    
    // If target location provided, calculate profit
    let profit = null;
    if (targetLocation) {
      profit = calculateProfit(good.id, currentLocation, targetLocation, 1);
    }
    
    return {
      ...good,
      buyPrice,
      profit
    };
  }).sort((a, b) => {
    // Sort by profit if available, otherwise by price
    if (a.profit && b.profit) {
      return b.profit.profitPercent - a.profit.profitPercent;
    }
    return a.buyPrice - b.buyPrice;
  });
};

/**
 * Get best trade goods to sell at current location
 */
export const getBestSells = (currentLocation, cargoList) => {
  return cargoList.map(({ goodId, quantity }) => {
    const good = tradeGoods[goodId];
    const sellPrice = calculatePrice(goodId, currentLocation, false);
    
    return {
      ...good,
      quantity,
      sellPrice,
      totalValue: sellPrice * quantity
    };
  }).sort((a, b) => b.sellPrice - a.sellPrice);
};

/**
 * Format price with credits symbol
 */
export const formatPrice = (price) => {
  return `${price.toLocaleString()} CR`;
};

/**
 * Format profit with color indicator
 */
export const formatProfit = (profit) => {
  const sign = profit >= 0 ? '+' : '';
  return `${sign}${profit.toLocaleString()} CR`;
};

const TradeUtilsExport = {
  getLocationType,
  calculatePrice,
  calculateProfit,
  getBestBuys,
  getBestSells,
  formatPrice,
  formatProfit
};

export default TradeUtilsExport;
